/**
 * Leviathan - Bulk tax class assignment
 * Quasar Prime LLC
 *
 * Every instance has a catalogue of products with no tax class, because the
 * field did not exist when they were created. A product with no tax class
 * matches no fuel-specific tax, so every invoice line for it is silently
 * untaxed — a diesel load carrying no diesel excise, with nothing on the
 * invoice to suggest anything is wrong.
 *
 * Classifying a few hundred products one form at a time is not realistic, so
 * this screen does it in one pass.
 *
 * SUGGESTIONS ARE OFFERED, NEVER APPLIED. Each row starts unset with a
 * suggestion beside it, and nothing is written until the operator picks. There
 * is deliberately no "accept all suggestions" button: a LOW-confidence guess
 * committed with one unread click is exactly how a catalogue ends up looking
 * classified and being wrong, and a wrong tax class that looks confirmed is
 * worse than an empty one that is visibly missing.
 */

import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { AlertTriangle, ArrowLeft, Check, Info, Loader2 } from 'lucide-react';
import { apiFetch } from '@/utils/api';
import { API_URL } from '@/config/api';
import { parseApiError } from '@/utils/errorHandler';
import { TAX_CLASS_OPTIONS, taxClassLabel } from '@/constants/productTaxClass';

interface UnclassifiedProduct {
  id: string;
  name: string;
  sku: string | null;
  category: string;
  productType: string | null;
  suggestion: { taxClass: string; confidence: 'HIGH' | 'MEDIUM' | 'LOW'; reason: string } | null;
}

const CONFIDENCE_STYLE: Record<string, string> = {
  HIGH: 'text-green-400 border-green-500/40',
  MEDIUM: 'text-amber-400 border-amber-500/40',
  LOW: 'text-slate-400 border-slate-600',
};

export default function TaxClassAssignment() {
  const [products, setProducts] = useState<UnclassifiedProduct[]>([]);
  const [choices, setChoices] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [banner, setBanner] = useState<string | null>(null);
  const [fuelWithoutClass, setFuelWithoutClass] = useState(0);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const response = await apiFetch(`${API_URL}/api/inventory/tax-class/unclassified`);
      if (!response.ok) throw new Error(await parseApiError(response));
      const data = await response.json();
      setProducts(data.products);
      setFuelWithoutClass(data.fuelWithoutClass);
      setChoices({});
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load products');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const save = async () => {
    const assignments = Object.entries(choices)
      .filter(([, taxClass]) => taxClass)
      .map(([productId, taxClass]) => ({ productId, taxClass }));

    if (assignments.length === 0) return;

    setSaving(true);
    try {
      const response = await apiFetch(`${API_URL}/api/inventory/tax-class/bulk`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ assignments }),
      });
      if (!response.ok) throw new Error(await parseApiError(response));

      const data = await response.json();
      setBanner(`${data.applied} product${data.applied === 1 ? '' : 's'} classified.`);
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const chosenCount = Object.values(choices).filter(Boolean).length;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-6 h-6 text-amber-500 animate-spin" />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      <div>
        <Link to="/inventory" className="text-amber-500 hover:underline text-sm inline-flex items-center gap-1">
          <ArrowLeft className="w-3 h-3" /> Back to Inventory
        </Link>
        <h1 className="text-2xl font-bold text-slate-100 mt-2">Assign Tax Classes</h1>
        <p className="text-slate-400 text-sm mt-1">
          Products that have no tax class and are therefore invoiced with no fuel tax.
        </p>
      </div>

      {error && (
        <div className="bg-red-500/10 border border-red-500/50 p-4 text-red-300 text-sm">{error}</div>
      )}
      {banner && (
        <div className="bg-green-500/10 border border-green-500/50 p-4 text-green-300 text-sm">
          {banner}
        </div>
      )}

      {products.length === 0 ? (
        <div className="bg-slate-800 border border-slate-700 p-8 text-center">
          <Check className="w-8 h-8 text-green-400 mx-auto mb-3" />
          <p className="text-slate-200">Every active product has a tax class.</p>
          <p className="text-slate-500 text-sm mt-1">
            That does not mean each one is <em>correct</em> — only that none is missing.
          </p>
        </div>
      ) : (
        <>
          {fuelWithoutClass > 0 && (
            <div className="flex items-start gap-3 bg-red-500/10 border border-red-500/50 p-4">
              <AlertTriangle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-slate-300">
                <p className="font-medium text-red-400 mb-1">
                  {fuelWithoutClass} FUEL product{fuelWithoutClass === 1 ? '' : 's'} unclassified
                </p>
                <p>
                  These are certainly taxable and certainly not being taxed. Every invoice raised
                  against them carries no excise at all, and nothing on the invoice says so.
                </p>
              </div>
            </div>
          )}

          <div className="flex items-start gap-3 bg-slate-800 border border-slate-700 p-4 text-sm text-slate-400">
            <Info className="w-4 h-4 text-slate-500 flex-shrink-0 mt-0.5" />
            <p>
              Suggestions come from the product name and are{' '}
              <strong className="text-slate-300">guesses</strong>. Check each one — clear and dyed
              diesel are taxed completely differently, and the names are a single word apart. There
              is no "accept all" button on purpose.
            </p>
          </div>

          <div className="bg-slate-800 border border-slate-700 overflow-x-auto">
            <table className="w-full">
              <thead className="bg-slate-900 border-b border-slate-700">
                <tr>
                  <th className="text-left px-4 py-3 text-slate-400 text-xs uppercase tracking-wider font-medium">
                    Product
                  </th>
                  <th className="text-left px-4 py-3 text-slate-400 text-xs uppercase tracking-wider font-medium">
                    Category
                  </th>
                  <th className="text-left px-4 py-3 text-slate-400 text-xs uppercase tracking-wider font-medium">
                    Suggestion
                  </th>
                  <th className="text-left px-4 py-3 text-slate-400 text-xs uppercase tracking-wider font-medium">
                    Tax Class
                  </th>
                </tr>
              </thead>
              <tbody>
                {products.map((product) => (
                  <tr key={product.id} className="border-b border-slate-700">
                    <td className="px-4 py-3">
                      <div className="text-slate-100 text-sm">{product.name}</div>
                      <div className="text-slate-500 text-xs font-mono">
                        {product.sku ?? 'no SKU'}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <span
                        className={`px-2 py-1 text-xs border ${
                          product.category === 'FUEL'
                            ? 'border-red-500/50 text-red-400 bg-red-500/10'
                            : 'border-slate-600 text-slate-400'
                        }`}
                      >
                        {product.category}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      {product.suggestion ? (
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() =>
                              setChoices((prev) => ({
                                ...prev,
                                [product.id]: product.suggestion!.taxClass,
                              }))
                            }
                            className={`px-2 py-1 text-xs border hover:bg-slate-700 ${
                              CONFIDENCE_STYLE[product.suggestion.confidence]
                            }`}
                            title={product.suggestion.reason}
                          >
                            {taxClassLabel(product.suggestion.taxClass)} &middot;{' '}
                            {product.suggestion.confidence.toLowerCase()}
                          </button>
                        </div>
                      ) : (
                        <span className="text-slate-600 text-xs">
                          No guess — the name says nothing specific
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <select
                        value={choices[product.id] ?? ''}
                        onChange={(e) =>
                          setChoices((prev) => ({ ...prev, [product.id]: e.target.value }))
                        }
                        className="bg-slate-900 border border-slate-700 focus:border-amber-500 px-2 py-1 text-slate-100 text-sm min-w-[180px]"
                      >
                        <option value="">— leave unset —</option>
                        {TAX_CLASS_OPTIONS.map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="flex items-center justify-between sticky bottom-0 bg-slate-900 border border-slate-700 p-4">
            <span className="text-slate-400 text-sm">
              {chosenCount} of {products.length} chosen
            </span>
            <button
              onClick={save}
              disabled={saving || chosenCount === 0}
              className="px-4 py-2 bg-amber-500 text-slate-900 font-medium hover:bg-amber-600 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              {saving ? 'Saving…' : `Apply ${chosenCount} classification${chosenCount === 1 ? '' : 's'}`}
            </button>
          </div>
        </>
      )}
    </div>
  );
}
